'use client';

import { useState } from 'react';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import ContactModal from '@/components/ui/ContactModal';
import Button from '@/components/ui/Button';
import BrandWordmark from '@/components/ui/BrandWordmark';
import { sendGAEvent } from '@next/third-parties/google';
import { useScrollSpy } from '@/lib/useScrollSpy';
import { Link, usePathname } from '@/navigation';
// Same icons as the floating bottom nav (SiteNavBar), reused in the mobile menu.
import { Zap, LayoutGrid, Paintbrush, DoorOpen, Users, Star, ShieldCheck, HelpCircle } from 'lucide-react';

const NAV_IDS = ['instalacje', 'plytki', 'malowanie', 'podlogi-drzwi', 'o-nas', 'opinie', 'gwarancje', 'faq'] as const;

export default function Header() {
  const t = useTranslations('nav');
  const tBrand = useTranslations('brand');
  const [modalOpen, setModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeId = useScrollSpy(NAV_IDS);
  const pathname = usePathname();

  // Brand → home/hero. On a sub-page (privacy, cookies) let the link navigate to
  // the homepage; when already on the homepage, smooth-scroll to the top instead.
  function goHome(e: React.MouseEvent) {
    if (pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMenuOpen(false);
  }

  const navItems = [
    { id: 'instalacje', label: t('electrical'), icon: Zap },
    { id: 'plytki', label: t('tile'), icon: LayoutGrid },
    { id: 'malowanie', label: t('painting'), icon: Paintbrush },
    { id: 'podlogi-drzwi', label: t('floors'), icon: DoorOpen },
    { id: 'o-nas', label: t('about'), icon: Users },
    { id: 'opinie', label: t('clients'), icon: Star },
    { id: 'gwarancje', label: t('guarantees'), icon: ShieldCheck },
    { id: 'faq', label: t('faq'), icon: HelpCircle },
  ];

  function scrollTo(id: string) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  }

  // Open the Quick Contact modal from a CTA. Fires both GA events: the primary
  // CTA click and the canonical "contact modal opened" signal (kept separate so
  // the modal-open count stays correct if other triggers open it later).
  function openContactModal(source: 'header' | 'mobile_menu') {
    sendGAEvent('event', 'cta_click', { cta_location: source });
    sendGAEvent('event', 'contact_modal_open', { source });
    setModalOpen(true);
  }

  return (
    <>
      {/* Full-screen frosted backdrop behind the open mobile menu so the page
          reads as a soft blurred layer instead of bleeding through around the
          panel. Sits above the floating bottom UI (dock/back-to-top/cookie) but
          below the header + menu (which get a raised z-index while open). Body
          isn't scroll-locked here, so backdrop-filter renders fine on iOS. */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[110] lg:hidden bg-surface/70 backdrop-blur-xl"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      <header
        className={clsx(
          'fixed top-0 w-full border-b border-outline-variant/10 backdrop-blur-xl transition-colors',
          // Raise above the backdrop and go more opaque while the menu is open so
          // the top bar (brand/lang/close) reads cleanly instead of washed-out.
          menuOpen ? 'z-[120] bg-surface/95' : 'z-50 bg-surface/80'
        )}
      >
        {/* Top row: brand + lang switcher + CTA. Taller on desktop (lg:py-6) so
            the floating NavBar pill sits inside the header band instead of
            spilling past its bottom edge. */}
        <div className="flex items-center justify-between px-margin-mobile lg:px-margin-desktop py-4 lg:py-6">
          {/* Brand → homepage hero. Inline SVG wordmark (BrandWordmark) mirrors
              the Footer brand, sized down for the header band. Gold is the
              logo's own brand colour (site `primary` is green, so it's set
              explicitly here); hover feedback comes from the brand-glow. */}
          <Link
            href="/"
            onClick={goHome}
            aria-label={tBrand('name')}
            className="group inline-flex items-center gap-2.5 font-headline font-bold text-label-caps tracking-[0.2em] text-[#e9c176] uppercase transition-colors focus-visible:outline-none"
          >
            <BrandWordmark className="h-8 w-auto shrink-0 brand-glow" />
          </Link>

          {/* Right: lang switcher + CTA */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button
              variant="primary"
              size="sm"
              arrow
              onClick={() => openContactModal('header')}
              className="hidden sm:inline-flex cta-gold-hover !bg-[#F5F0E8]"
            >
              {t('cta')}
            </Button>
            {/* Mobile menu toggle */}
            <button
              className="lg:hidden text-on-surface p-1"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                {menuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round"/>
                ) : (
                  <>
                    <path d="M4 6h16" strokeLinecap="round"/>
                    <path d="M4 12h16" strokeLinecap="round"/>
                    <path d="M4 18h16" strokeLinecap="round"/>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Desktop primary nav now lives in the floating <NavBar> (SiteNavBar),
            mounted on the page. The mobile dropdown below stays — it also holds
            the CTA on small screens where the top-row button is hidden. */}

        {/* Mobile nav dropdown */}
        {menuOpen && (
          <nav className="lg:hidden border-t border-outline-variant/10 bg-surface/95 backdrop-blur-xl">
            <ul className="px-margin-mobile py-4 space-y-1">
              {navItems.map(({ id, label, icon: Icon }) => (
                <li key={id}>
                  <button
                    onClick={() => scrollTo(id)}
                    className={`flex w-full items-center gap-3 text-left py-3 text-body-md transition-colors hover:text-primary ${
                      activeId === id ? 'text-primary' : 'text-on-surface-variant'
                    }`}
                  >
                    <Icon size={20} strokeWidth={2} className="shrink-0" aria-hidden />
                    {label}
                  </button>
                </li>
              ))}
              <li className="pt-3">
                <Button variant="primary" fullWidth arrow className="cta-gold-hover !bg-[#F5F0E8]" onClick={() => { openContactModal('mobile_menu'); setMenuOpen(false); }}>
                  {t('cta')}
                </Button>
              </li>
            </ul>
          </nav>
        )}
      </header>

      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
